import React, { FC, ReactNode, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import {
    FormattedMessage,
    PageTitle,
    Table,
    useToast,
    ToastLevel,
    useIntl,
    Button,
    Icon,
    Grid,
    GridColumn,
} from "@metsooutotec/modes-react-components"
import customerStore from "features/organizations/customers/store/customer-store"
import AddressView from "features/organizations/sites/utils/address-view"
import { ModalTypes } from "../../customers/utils/customer-modal-helper"
import EntityDeleteModal from "../../customers/components/entity-delete-modal"
import { NotFound } from "../../../../components/utils/not-found"
import SiteModal from "../components/site-modal"
import { GenerateBreadcrumbs } from "../../customers/utils/customers-bread-crumbs"
import { NavigationModalOptions, NavigationDeleteEntityOptions } from "../../../../components/utils/modal-helper"
import {
    itemViewCellStyles,
    itemViewDetailsContainerStyles,
    itemViewDetailsTableStyles,
    itemViewListContainerStyles,
    itemViewSeparatorStyles,
    itemViewSubtitleStyles,
} from "../../../../components/common-style/common-style"
import { Site } from "../models/sites-models"
import AssetsCard from "../components/assets-card/assets-card"
import UsersCard from "features/organizations/common/cards/users-cards/users-card"

const SiteView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { customerId, siteId } = useParams()
    const { setMessage } = useToast()
    const location = useLocation()
    const [isSiteModalOpen, setSiteModalOpen] = useState(false)
    //if siteToEdit is undefinded the modal will open with empty fields to create a new site.
    const [siteToEdit, setSiteToEdit] = useState<Site>()
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const params: NavigationModalOptions = location.state
    const deleteParams: NavigationDeleteEntityOptions = location.state
    const { deleted: DeletedAsset, entityName: AssetName } = deleteParams || {}
    const { customerMap, siteMap } = customerStore
    const customer = customerMap[customerId || ""]
    const site = siteMap[siteId || ""]

    const showNotifier = (text: string) => {
        setMessage(text, ToastLevel.SUCCESS, 6000)
    }
    useEffect(() => {
        if (params?.type === "created") {
            showNotifier("New site created successfully")
        }
        if (params?.type === "edited") {
            showNotifier("All changes made successfully")
        }
        window.history.replaceState({}, document.title)
    }, [params])

    useEffect(() => {
        if (DeletedAsset) {
            showNotifier(`You have deleted Asset '${AssetName}'.`)
        }
        window.history.replaceState({}, document.title)
    }, [DeletedAsset, AssetName, deleteParams])

    const siteModal = {
        open: () => {
            setSiteModalOpen(true)
            setSiteToEdit(site)
        },
        close: (passed: boolean, siteId?: string, navigationCustomerOptions?: NavigationModalOptions) => {
            setSiteModalOpen(false)
            setSiteToEdit(undefined)
            if (passed) {
                navigate(`/customers/${customerId}/sites/${siteId}`, {
                    state: navigationCustomerOptions,
                })
            }
        },
    }
    const deleteSiteModal = {
        open: () => {
            setIsDeleteModalOpen(true)
        },
        close: (params: NavigationDeleteEntityOptions) => {
            setIsDeleteModalOpen(false)
            if (params.deleted) {
                navigate(`/customers/${customerId}`, { state: params })
            }
        },
    }

    const breadcrumbsPath = GenerateBreadcrumbs(customer, site, undefined, undefined)

    const rightPageButtons: ReactNode = (
        <>
            <Button icon='icon-edit-16' labelPosition='right' onClick={siteModal.open} primary>
                {formatMessage("site.edit")}
            </Button>
            <Button icon='icon-trash-16' labelPosition='left' onClick={deleteSiteModal.open} secondary>
                {formatMessage("site.delete")}
            </Button>
        </>
    )
    return !site ? (
        <NotFound id={siteId} />
    ) : (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={site.name} extraItems={rightPageButtons} />
            <div style={itemViewDetailsContainerStyles}>
                <h3 style={itemViewSubtitleStyles}>
                    <FormattedMessage id='site.details' />
                </h3>
                <Table style={itemViewDetailsTableStyles} tight>
                    <Table.Body>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='headings.address' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>
                                <AddressView address={site.address} />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='site.utcOffsetHours' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{site.utcOffsetHours}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='site.longitude' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{site.address?.longitude}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='site.latitude' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{site.address?.latitude}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='headings.contact' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{site.contact}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.HeaderCell style={itemViewCellStyles}>
                                <FormattedMessage id='headings.metsoContact' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{site.metsoContact}</Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
            </div>
            <div style={itemViewSeparatorStyles}></div>
            <div style={itemViewListContainerStyles}>
                <Grid columns={2}>
                    <GridColumn>
                        <AssetsCard />
                    </GridColumn>
                    <GridColumn>
                        <UsersCard moduleType='site' id={siteId as string} />
                    </GridColumn>
                </Grid>
            </div>
            <SiteModal isOpen={isSiteModalOpen} onClose={siteModal.close} site={siteToEdit} customerId={customer?.id} />
            <EntityDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={deleteSiteModal.close}
                entity={site}
                modalType={ModalTypes.SiteModal}
                customerId={customer?.id}
            />
        </>
    )
})

export default SiteView
